import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { FiCalendar, FiShare2, FiUser } from 'react-icons/fi';
import { format } from 'date-fns';
import PageTitle from '../../components/PageTitle';
import { Helmet } from 'react-helmet-async';

/**
 * BlogPost interface for blog post data.
 */
interface BlogPost {
  id: string;
  title: string;
  content: string;
  authorId: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  scheduledAt: Timestamp;
  isPublished: boolean;
  tags: string[];
  coverImageUrl?: string;
  excerpt?: string;
}

/**
 * Formats a Firestore Timestamp to a readable string.
 * @param timestamp - Firestore Timestamp
 * @returns formatted date string
 */
function formatDate(timestamp: Timestamp): string {
  try {
    return format(timestamp.toDate(), 'MMMM d, yyyy h:mm a');
  } catch {
    return '';
  }
}

/**
 * Copies the current page URL to the clipboard.
 * Shows a toast if successful.
 */
function copyToClipboard(text: string): void {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text);
  }
}

/**
 * BlogPostDetails component displays the details of a single blog post.
 * Fetches data from the blogPost collection using the id from the route params.
 */


const BlogPostDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);

  useEffect(() => {
    const fetchPost = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const postDoc = await getDoc(doc(db, 'blogPost', id));
        if (!postDoc.exists()) {
          setError('Blog post not found');
          return;
        }
        setPost({ id: postDoc.id, ...postDoc.data() } as BlogPost);
      } catch {
        setError('Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [id]);

  /**
   * Handles sharing the blog post by copying the URL.
   */
  function handleShare(): void {
    copyToClipboard(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }
  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <p className="text-xl mb-4">{error || 'Blog post not found'}</p>
          <button
            onClick={() => navigate('/events')}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Helmet>
        <title>{post.title} | Blog | SS Solar</title>
        <meta name="description" content={post.excerpt || post.title} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || post.title} />
        {post.coverImageUrl && <meta property="og:image" content={post.coverImageUrl} />}
      </Helmet>
      <PageTitle
        title={post.title}
        subtitle={post.excerpt || 'Explore how solar panels are changing the way we power our homes, businesses, and planet—one sunbeam at a time.'}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-gray-800 rounded-lg overflow-hidden shadow-xl">
          {post.coverImageUrl ? (
            <img
              src={post.coverImageUrl}
              alt={post.title}
              className="w-full h-96 object-cover"
            />
          ) : (
            <div className="w-full h-96 bg-gray-700 flex items-center justify-center">
              <FiCalendar className="w-24 h-24 text-gray-500" />
            </div>
          )}
          <div className="p-8">
            <div className="flex flex-wrap justify-between items-center mb-6">
              <div className="flex items-center gap-3 text-gray-400 text-sm">
                <FiUser className="inline-block mr-1" aria-label="Author" />
                <span>{post.authorId}</span>
                <FiCalendar className="inline-block ml-4 mr-1" aria-label="Published date" />
                <span>{formatDate(post.createdAt)}</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {(Array.isArray(post.tags) ? post.tags : []).map((tag) => (
                  <span key={tag} className="px-2 py-1 bg-indigo-700 rounded-full text-xs text-white" aria-label={`Tag ${tag}`}>#{tag}</span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-3 py-2 bg-gray-700 text-white rounded hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                aria-label="Share blog post"
              >
                <FiShare2 />
                {copied ? 'Copied!' : 'Share'}
              </button>
            </div>
            <div className="text-gray-100 text-lg mb-6">{post.excerpt}</div>
            <div className="prose prose-invert max-w-none mb-8 text-gray-100">
              <div dangerouslySetInnerHTML={{ __html: post.content }} />
            </div>
            <div className="flex gap-4 mt-8">
              <button
                onClick={() => navigate('/events')}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                aria-label="Back to blog list"
              >
                Back to Blog List
              </button>
            </div>
          </div>
        </div>
      </div>
      {process.env.NODE_ENV !== 'production' && (
        <div style={{ position: 'fixed', bottom: 0, right: 0, background: 'rgba(31,41,55,0.9)', color: '#fff', fontSize: '0.875rem', padding: '0.75rem 1.25rem', borderRadius: '0.5rem 0 0 0', zIndex: 50 }}>
          <div><strong>Debug Info</strong></div>
          <div><strong>id:</strong> {id}</div>
          <div><strong>loading:</strong> {String(loading)}</div>
          <div><strong>error:</strong> {error ?? 'none'}</div>
          <div><strong>post:</strong> {post ? post.title : 'none'}</div>
        </div>
      )}
    </div>
  );
};

export default BlogPostDetails;
